import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

const MIN_EDITOR_PX = 240;
const MIN_FILES_PX = 160;
const MAX_FILES_PX = 440;
const MIN_CONSOLE_PX = 220;
const MAX_CONSOLE_PX = 640;
const SPLITTER_PX = 6;
const COLLAPSED_FILES_PX = 40;
const DEFAULT_FILES_PX = 240;
const DEFAULT_CONSOLE_PX = 340;

export type PlaygroundPanelDragTarget = "files" | "console";

export interface PlaygroundPanelSizes {
  readonly wideLayout: boolean;
  readonly dragging: PlaygroundPanelDragTarget | null;
  readonly gridTemplateColumns: string | undefined;
  readonly onFilesSplitterPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
  readonly onConsoleSplitterPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
  readonly showFilesSplitter: boolean;
}

function storageKeyFor(base?: string): string {
  return `ccs-playground-panel-sizes:${base?.trim() || "default"}`;
}

function loadStoredSizes(base?: string): { files: number; console: number } | null {
  try {
    const raw = localStorage.getItem(storageKeyFor(base));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { files?: unknown; console?: unknown };
    const files = typeof parsed.files === "number" ? parsed.files : null;
    const console = typeof parsed.console === "number" ? parsed.console : null;
    if (files === null || console === null) return null;
    return {
      files: Math.min(MAX_FILES_PX, Math.max(MIN_FILES_PX, files)),
      console: Math.min(MAX_CONSOLE_PX, Math.max(MIN_CONSOLE_PX, console)),
    };
  } catch {
    return null;
  }
}

function persistSizes(base: string | undefined, files: number, console: number) {
  try {
    localStorage.setItem(storageKeyFor(base), JSON.stringify({ files, console }));
  } catch {
    /* ignore quota errors */
  }
}

function clampFiles(width: number, totalWidth: number, consoleWidth: number): number {
  const max = totalWidth - consoleWidth - 2 * SPLITTER_PX - MIN_EDITOR_PX;
  return Math.min(MAX_FILES_PX, Math.max(MIN_FILES_PX, Math.min(width, max)));
}

function clampConsole(width: number, totalWidth: number, filesWidth: number): number {
  const max = totalWidth - filesWidth - 2 * SPLITTER_PX - MIN_EDITOR_PX;
  return Math.min(MAX_CONSOLE_PX, Math.max(MIN_CONSOLE_PX, Math.min(width, max)));
}

function clampConsoleCollapsed(width: number, totalWidth: number): number {
  const max = totalWidth - COLLAPSED_FILES_PX - SPLITTER_PX - MIN_EDITOR_PX;
  return Math.min(MAX_CONSOLE_PX, Math.max(MIN_CONSOLE_PX, Math.min(width, max)));
}

function buildGridColumns(filesWidth: number, consoleWidth: number): string {
  return `${filesWidth}px ${SPLITTER_PX}px minmax(0, 1fr) ${SPLITTER_PX}px ${consoleWidth}px`;
}

function buildGridColumnsCollapsed(consoleWidth: number): string {
  return `${COLLAPSED_FILES_PX}px minmax(0, 1fr) ${SPLITTER_PX}px ${consoleWidth}px`;
}

export function usePlaygroundPanelSizes(
  panelRef: RefObject<HTMLElement | null>,
  opts: { storageKey?: string; filesCollapsed: boolean; onResize?: () => void }
): PlaygroundPanelSizes {
  const stored = loadStoredSizes(opts.storageKey);
  const [filesWidth, setFilesWidth] = useState(stored?.files ?? DEFAULT_FILES_PX);
  const [consoleWidth, setConsoleWidth] = useState(stored?.console ?? DEFAULT_CONSOLE_PX);
  const [dragging, setDragging] = useState<PlaygroundPanelDragTarget | null>(null);
  const [wideLayout, setWideLayout] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 900px)").matches : false
  );

  const sizesRef = useRef({ files: filesWidth, console: consoleWidth });
  sizesRef.current = { files: filesWidth, console: consoleWidth };

  const dragRef = useRef<{
    target: PlaygroundPanelDragTarget;
    startX: number;
    startFiles: number;
    startConsole: number;
  } | null>(null);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 900px)");
    const onChange = () => setWideLayout(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };
  }, [dragging]);

  const finishDrag = useCallback(() => {
    const drag = dragRef.current;
    dragRef.current = null;
    setDragging(null);
    if (drag) {
      persistSizes(opts.storageKey, sizesRef.current.files, sizesRef.current.console);
    }
  }, [opts.storageKey]);

  useEffect(() => {
    if (!dragging) return;

    const onMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      const panel = panelRef.current;
      if (!drag || !panel) return;

      const totalWidth = panel.getBoundingClientRect().width;
      const deltaX = event.clientX - drag.startX;
      const filesSide = opts.filesCollapsed ? COLLAPSED_FILES_PX : sizesRef.current.files;

      if (drag.target === "files") {
        const next = clampFiles(drag.startFiles + deltaX, totalWidth, sizesRef.current.console);
        sizesRef.current = { ...sizesRef.current, files: next };
        setFilesWidth(next);
      } else {
        const next = opts.filesCollapsed
          ? clampConsoleCollapsed(drag.startConsole - deltaX, totalWidth)
          : clampConsole(drag.startConsole - deltaX, totalWidth, filesSide);
        sizesRef.current = { ...sizesRef.current, console: next };
        setConsoleWidth(next);
      }
      opts.onResize?.();
    };

    const onUp = () => finishDrag();

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [dragging, finishDrag, opts, panelRef]);

  useEffect(() => {
    if (!wideLayout) return;
    const panel = panelRef.current;
    if (!panel) return;

    const clampToPanel = () => {
      const totalWidth = panel.getBoundingClientRect().width;
      if (totalWidth <= 0) return;
      const filesSide = opts.filesCollapsed ? COLLAPSED_FILES_PX : sizesRef.current.files;
      const nextFiles = opts.filesCollapsed
        ? sizesRef.current.files
        : clampFiles(sizesRef.current.files, totalWidth, sizesRef.current.console);
      const nextConsole = opts.filesCollapsed
        ? clampConsoleCollapsed(sizesRef.current.console, totalWidth)
        : clampConsole(sizesRef.current.console, totalWidth, filesSide);
      sizesRef.current = { files: nextFiles, console: nextConsole };
      setFilesWidth(nextFiles);
      setConsoleWidth(nextConsole);
    };

    clampToPanel();
    const ro = new ResizeObserver(clampToPanel);
    ro.observe(panel);
    return () => ro.disconnect();
  }, [opts.filesCollapsed, panelRef, wideLayout]);

  const startDrag = useCallback(
    (target: PlaygroundPanelDragTarget, event: React.PointerEvent<HTMLDivElement>) => {
      if (!wideLayout || (target === "files" && opts.filesCollapsed)) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      dragRef.current = {
        target,
        startX: event.clientX,
        startFiles: sizesRef.current.files,
        startConsole: sizesRef.current.console,
      };
      setDragging(target);
    },
    [opts.filesCollapsed, wideLayout]
  );

  const gridTemplateColumns = wideLayout
    ? opts.filesCollapsed
      ? buildGridColumnsCollapsed(consoleWidth)
      : buildGridColumns(filesWidth, consoleWidth)
    : undefined;

  return {
    wideLayout,
    dragging,
    gridTemplateColumns,
    onFilesSplitterPointerDown: (event) => startDrag("files", event),
    onConsoleSplitterPointerDown: (event) => startDrag("console", event),
    showFilesSplitter: wideLayout && !opts.filesCollapsed,
  };
}
